import React, { useState, useEffect } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { darkwebService } from '@app/services';
import { AxiosResponse } from 'axios';
import { infiniteScrollItemsFetchLimit } from '@app/helpers';
import { confirm, PasslyButton, PasslySelect, PasslyTable, PasslyTableCell } from '@app/components';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { useDispatch } from 'react-redux';
import { handleExpireUserPassword } from '@app/store/actions';
import { ITableHead } from '@app/models';

interface IProps {
    reportId: string;
}

const reportStatusOptions = [
    { name: 'DARKWEB_COMPROMISE_DEFAULT_SELECT_PLACEHOLDER', id: 'NotSet' },
    { name: 'DARKWEB_COMPROMISE_RESOLVED', id: 'Resolved' },
    { name: 'DARKWEB_COMPROMISE_IGNORED', id: 'Ignored' }
];

enum TableHeadEnum {
    identityName = 'identityName',
    compromisedAt = 'compromisedAt',
    expirePasswordButton = 'expirePasswordButton',
    reportStatus = 'reportStatus'
}

export const DarkwebUsersTable: React.SFC<IProps> = ({ reportId }) => {
    const { translation } = useTypedTranslation();
    const [rows, setRows] = useState<any>([]);
    const [isShowLoader, setIsShowLoader] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [reset, setReset] = useState(0);
    const dispatch = useDispatch();

    const getComplexExpirePasswordButtonCell = (row: any) => {
        return (
            <PasslyTableCell>
                <PasslyButton
                    id='expirePasswordButton'
                    variant='contained'
                    color='primary'
                    onClick={handleExpirePassword(row.identityId, row.id)}
                    disableRipple={true}
                    disabled={row.status === 'Resolved'}
                >
                    {translation('EXPIRE_PASSWORD')}
                </PasslyButton>
            </PasslyTableCell>
        );
    };

    const getComplexReportStatusCell = (row: any) => {
        return (
            <PasslyTableCell>
                <PasslySelect
                    id='reportStatus'
                    name='reportStatus'
                    label=''
                    value={row.status}
                    onChange={handleSelectChange(row.id)}
                    displayEmpty
                    disabled={row.status === 'Resolved'}
                    options={reportStatusOptions}
                    optionLabel='name'
                    optionValue='id'
                    appendTo={document.body}
                />
            </PasslyTableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.identityName,
            label: 'DARKWEB_COMPROMISE_USERS_TABLE_HEADER_1'
        },
        {
            id: TableHeadEnum.compromisedAt,
            label: 'DARKWEB_COMPROMISE_USERS_TABLE_HEADER_2',
            type: 'dateTime'
        },
        {
            id: TableHeadEnum.expirePasswordButton,
            label: 'DARKWEB_COMPROMISE_USERS_TABLE_HEADER_3',
            type: 'getComplexCell',
            getComplexCell: getComplexExpirePasswordButtonCell
        },
        {
            id: TableHeadEnum.reportStatus,
            label: 'DARKWEB_COMPROMISE_USERS_TABLE_HEADER_4',
            type: 'getComplexCell',
            getComplexCell: getComplexReportStatusCell
        }
    ];

    const handleSuccess = (isReset: boolean) => (res: AxiosResponse) => {
        setIsShowLoader(false);

        if (res.data.items && !isReset) {
            setRows((previousState: any) => previousState.concat(res.data.items));
        }

        if (isReset) {
            setRows(res.data.items);
            setTotalCount(res.data.count);
        }
    };

    const handleError = (error: any) => {
        setIsShowLoader(false);
        setErrorMessage(error.response.data.message);
    };

    useEffect(() => {
        setReset(reset + 1);
    }, [reportId]);

    useEffect(() => {
        if (errorMessage) {
            dispatch(handleSetSnackbar({ show: true, message: errorMessage, severity: 'error' }));
        }
    }, [errorMessage]);

    const handleExpirePassword = (userId: string, id: string) => () => {
        confirm(translation('EXPIRE_PASSWORD_BODY'), translation('OK'), translation('CANCEL'), translation('EXPIRE_PASSWORD_HEADER')).then(
            () => {
                const newRows = [...rows];
                const currentRow = newRows.find((row) => row.id === id);

                currentRow.status = 'Resolved';
                setRows(newRows);

                dispatch(handleExpireUserPassword(userId));
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleSelectChange = (id: string) => (e: any) => {
        const status = e.target.value;
        const newRows = [...rows];
        const currentRow = newRows.find((row) => row.id === id);

        currentRow.status = status;
        darkwebService.updateCompromisedUserStatus(id, status);
        setRows(newRows);
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (!reportId) {
            return;
        }

        setIsShowLoader(isReset);
        darkwebService.getCompromisedUsers(reportId, skipCount, infiniteScrollItemsFetchLimit, handleError, handleSuccess(isReset));
    };

    return (
        <>
            <PasslyTable
                rows={rows}
                setRows={setRows}
                headerItems={headCells}
                rowsCount={totalCount}
                onFetch={fetchMoreData}
                withCheckboxes={false}
                emptyListHelper={translation('DARKWEB_COMPROMISE_EMPTY_COMPROMISED_LIST')}
                loadingData={isShowLoader}
                resetLocal={reset}
                hideFilter={true}
            />
        </>
    );
};
